"use client";

import React, { useState, useRef, useEffect } from "react";

import Page from "@/components/ui/page";
import Section from "@/components/ui/section";
import { useTina } from "@/utils";
import Header from "@/components/Header";
import CallToAction from "@/components/CallToAction";
import { Check, Info } from "lucide-react";
import { cn, tinaFieldSafe } from "@/utils";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import FAQs from "@/components/FAQs";
import LogosCarousel from "@/components/LogosCarousel";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import HighlightedText from "@/components/ui/highlighted-text";
import { motion } from "motion/react";
import { useGlobalData } from "@/components/TinaContext";

export default function Packages(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const header = data?.packages?.header;
  const plans = data?.packages?.plans;
  const sections = data?.packages?.sections;
  const customers = data?.packages?.customers;
  const faqsSection = data?.packages?.faqs;
  const callToAction = data?.packages?.cta;
  return (
    <Page
      defaultTinaField={data?.packages}
      seo={data?.packages?.seo}
      pagePath="/packages"
      titleFallback="Packages"
      faqs={data?.packages?.faqs}
    >
      <Header {...header} variant="left" />
      <PackagesTable plans={plans} sections={sections} />
      <FAQsSection faqsSection={faqsSection} />
      <CustomersSection customers={customers} />
      <LogosCarousel />
      <CallToAction
        {...callToAction}
        variant="left"
        backgroundVariant="dots"
      />
    </Page>
  );
}

function PackagesTable({ plans, sections }: { plans: any; sections: any }) {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  // Sentinel placed right after the desktop column-headers row. When it scrolls
  // above the viewport top the IntersectionObserver flips `pastHeader = true`.
  const pastHeaderSentinelRef = useRef<HTMLDivElement>(null);
  // Sentinel positioned 100px from the bottom of the table wrapper. When it
  // scrolls above the viewport top we leave the table; flips `withinTable = false`.
  const withinTableSentinelRef = useRef<HTMLDivElement>(null);
  const [pastHeader, setPastHeader] = useState(false);
  const [withinTable, setWithinTable] = useState(true);
  const showSticky = pastHeader && withinTable;
  const [navbarVisible, setNavbarVisible] = useState(true);
  const [selectedMobilePlan, setSelectedMobilePlan] = useState(0);
  const lastScrollY = useRef(0);

  // Use IntersectionObserver instead of `getBoundingClientRect()` in a scroll
  // listener. The observer callback receives `entry.boundingClientRect` from the
  // browser without forcing a synchronous layout, removing this component as a
  // forced-reflow source identified in the Lighthouse "Avoid large layout
  // shifts / Forced reflow" diagnostic.
  useEffect(() => {
    const pastHeaderSentinel = pastHeaderSentinelRef.current;
    const withinTableSentinel = withinTableSentinelRef.current;
    if (!pastHeaderSentinel || !withinTableSentinel) return;

    const pastHeaderObs = new IntersectionObserver(([entry]) => {
      setPastHeader(
        !entry.isIntersecting && entry.boundingClientRect.top < 0
      );
    });
    pastHeaderObs.observe(pastHeaderSentinel);

    const withinTableObs = new IntersectionObserver(([entry]) => {
      setWithinTable(
        entry.isIntersecting || entry.boundingClientRect.top > 0
      );
    });
    withinTableObs.observe(withinTableSentinel);

    return () => {
      pastHeaderObs.disconnect();
      withinTableObs.disconnect();
    };
  }, []);

  // Navbar visibility tracking still needs a scroll listener (it mirrors the
  // global Navbar's show/hide rule). Reading `window.scrollY` does NOT force
  // layout, but we still coalesce updates to one per frame via rAF so React
  // setState fires at most once per paint.
  useEffect(() => {
    let frame: number | null = null;
    const handleScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        const currentScrollY = window.scrollY;
        if (currentScrollY < lastScrollY.current) {
          setNavbarVisible(true);
        } else if (
          currentScrollY > 120 &&
          currentScrollY > lastScrollY.current
        ) {
          setNavbarVisible(false);
        }
        lastScrollY.current = currentScrollY;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div ref={tableWrapperRef} className="relative">
      {/* Bottom-of-table sentinel for IntersectionObserver. Positioned 100 px
          from the bottom of the wrapper, this preserves the original
          `tableRect.bottom > 100` margin from the old scroll handler. */}
      <div
        ref={withinTableSentinelRef}
        aria-hidden="true"
        className="absolute left-0 right-0 h-px pointer-events-none"
        style={{ bottom: "100px" }}
      />
      <Section wrapperClassName="!pt-12 relative">
        {/* Desktop Sticky Header */}
        <motion.div
          initial={{ y: -200 }}
          animate={{
            y: showSticky ? (navbarVisible ? 72 : 0) : -200,
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="hidden md:block fixed top-0 left-0 right-0 z-40 bg-background/0 backdrop-blur-md border-b border-border/50 py-1"
        >
          <div className="content py-4">
            <div
              className="grid gap-10 items-center"
              style={{
                gridTemplateColumns: `300px repeat(${plans.length}, 1fr)`,
              }}
            >
              <div className="text-sm text-muted-foreground font-mono uppercase"></div>
              {plans.map((plan, index) => (
                <div key={index} className="flex flex-col gap-4 items-start">
                  <h4 className="h5">{plan.name}</h4>
                  {plan?.button && (
                    <Button
                      variant={index === 0 ? "default" : "outline"}
                      size="sm"
                      href={plan?.button?.link}
                    >
                      {plan?.button?.label}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Mobile Plan Selector */}
        <div className="md:hidden space-y-4 mb-8">
          <div className="flex gap-3 overflow-x-auto pt-1 pb-6 -mx-6 px-6 border-b border-border">
            {plans.map((plan, index) => (
              <Button
                key={index}
                variant={"outline"}
                // className={cn("!py-1", {
                //   "ring-2 ring-secondary-400": selectedFilters.includes(
                //     resourceType.value
                //   ),
                // })}
                size="sm"
                onClick={() => setSelectedMobilePlan(index)}
                className={cn("whitespace-nowrap flex-shrink-0", {
                  "ring-2 ring-secondary-400": selectedMobilePlan === index,
                })}
              >
                {plan.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-6">
          {plans.map((plan, planIndex) => (
            <div
              key={planIndex}
              className={cn(
                "space-y-8",
                selectedMobilePlan !== planIndex && "hidden"
              )}
            >
              {/* Plan Header */}
              <div className="space-y-4 pb-6 ">
                <h3
                  className="h4"
                  data-tina-field={tinaFieldSafe(plan, "name")}
                >
                  {plan.name}
                </h3>
                <p
                  className="p-sm"
                  data-tina-field={tinaFieldSafe(plan, "description")}
                >
                  {plan?.description}
                </p>
                {plan?.button && (
                  <Button
                    href={plan?.button?.link}
                    variant={planIndex === 0 ? "default" : "outline"}
                    size="sm"
                    className="w-full"
                    data-tina-field={tinaFieldSafe(plan, "button")}
                  >
                    {plan?.button?.label}
                  </Button>
                )}
              </div>

              {/* Plan Features */}
              {sections.map((section, sectionIndex) => (
                <div key={sectionIndex} className="space-y-4">
                  <div className="flex items-center justify-between border-b border-border pb-2">
                    <h4
                      className="font-mono text-primary-200 uppercase text-sm"
                      data-tina-field={tinaFieldSafe(section, "title")}
                    >
                      {section.title}
                    </h4>
                    {section.learnMoreLink && (
                      <Button
                        variant="link"
                        size="sm"
                        className="decoration-secondary-500 underline text-xs"
                        data-tina-field={tinaFieldSafe(
                          section,
                          "learnMoreLink"
                        )}
                        href={section?.learnMoreLink}
                      >
                        Learn more
                      </Button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {section.features.map((feature, featureIndex) => {
                      const isEnabled = feature.enabledPlans?.includes(plan.id);
                      return (
                        <div
                          key={featureIndex}
                          className="flex items-start gap-3 py-2"
                        >
                          <Check
                            className={cn(
                              "size-5 stroke-[1.5px] flex-shrink-0 mt-0.5",
                              isEnabled
                                ? "text-secondary-500"
                                : "text-gray-500 opacity-50"
                            )}
                          />
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            <span
                              className={cn(
                                "text-sm",
                                !isEnabled && "text-muted-foreground"
                              )}
                              data-tina-field={tinaFieldSafe(feature, "name")}
                            >
                              {feature.name}
                            </span>
                            {feature.tooltip && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="size-4 text-muted-foreground cursor-help hover:text-white transition-colors flex-shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="max-w-[240px]"
                                  data-tina-field={tinaFieldSafe(
                                    feature,
                                    "tooltip"
                                  )}
                                >
                                  {feature.tooltip}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block w-full overflow-x-auto relative">
          <div className="relative">
            <div
              className="grid gap-10 mb-4"
              style={{
                gridTemplateColumns: `300px repeat(${plans.length}, 1fr)`,
              }}
            >
              <div />
              {plans.map((plan, index) => (
                <div key={index} className="flex flex-col gap-1 items-start">
                  <h3
                    className="h4"
                    data-tina-field={tinaFieldSafe(plan, "name")}
                  >
                    {plan.name}
                  </h3>
                  <p
                    className="p-sm max-w-[280px] break-words"
                    data-tina-field={tinaFieldSafe(plan, "description")}
                  >
                    {plan?.description}
                  </p>
                  {plan?.button && (
                    <Button
                      href={plan?.button?.link}
                      variant={index === 0 ? "default" : "outline"}
                      className="mt-4"
                      size="sm"
                      data-tina-field={tinaFieldSafe(plan, "button")}
                    >
                      {plan?.button?.label}
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {/* IntersectionObserver sentinel placed directly under the desktop
                column-headers row. `pastHeader` flips true the moment this 1 px
                strip scrolls above the viewport top. */}
            <div
              ref={pastHeaderSentinelRef}
              aria-hidden="true"
              className="h-px w-px"
            />

            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="mb-8">
                <div className="mb-4 flex items-center justify-between border-b border-border pb-2">
                  <h4
                    className="font-mono text-primary-200 uppercase"
                    data-tina-field={tinaFieldSafe(section, "title")}
                  >
                    {section.title}
                  </h4>
                  {section.learnMoreLink && (
                    <Button
                      variant="link"
                      size="sm"
                      className="decoration-secondary-500 underline"
                      data-tina-field={tinaFieldSafe(section, "learnMoreLink")}
                      href={section?.learnMoreLink}
                    >
                      Learn more
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  {section.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className="grid gap-10 items-center py-2 hover:bg-background-light transition-colors"
                      style={{
                        gridTemplateColumns: `300px repeat(${plans.length}, 1fr)`,
                      }}
                    >
                      {/* Feature Name */}
                      <div className="flex items-center gap-3">
                        <span
                          className="font-medium"
                          data-tina-field={tinaFieldSafe(feature, "name")}
                        >
                          {feature.name}
                        </span>
                        {feature.tooltip && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="size-4 text-muted-foreground cursor-help hover:text-white transition-colors" />
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="max-w-[240px]"
                              data-tina-field={tinaFieldSafe(
                                feature,
                                "tooltip"
                              )}
                            >
                              {feature.tooltip}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>

                      {plans.map((plan) => (
                        <div
                          key={plan.id}
                          data-tina-field={tinaFieldSafe(
                            feature,
                            "enabledPlans"
                          )}
                        >
                          <Check
                            className={cn(
                              "size-7 stroke-[1.5px]",
                              feature.enabledPlans?.includes(plan.id)
                                ? "text-secondary-500"
                                : "text-gray-500 opacity-50"
                            )}
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}

function FAQsSection({ faqsSection }: { faqsSection: any }) {
  if (!faqsSection?.showFaqs) {
    return null;
  }
  return (
    <Section backgroundVariant="pixels" className="space-y-8">
      <h2
        className="h1 text-center"
        data-tina-field={tinaFieldSafe(faqsSection, "title")}
      >
        {faqsSection?.title}
      </h2>
      <FAQs faqs={faqsSection?.faqs} />
    </Section>
  );
}

function CustomersSection({ customers }: { customers: any }) {
  const globalData = useGlobalData();
  const testimonials = globalData?.testimonials?.testimonials || [];

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  return (
    <Section
      wrapperClassName="!pb-40 md:!pb-64 overflow-hidden"
      className="space-y-12 !overflow-visible"
    >
      <div className="space-y-1">
        {customers?.title && (
          <HighlightedText
            as="h2"
            className="h2 text-balance"
            text={customers.title}
            data-tina-field={tinaFieldSafe(customers, "title")}
          />
        )}
        <p data-tina-field={tinaFieldSafe(customers, "subtitle")}>
          {customers.subtitle}
        </p>
      </div>
      <TestimonialsCarousel testimonials={testimonials} />
    </Section>
  );
}
